import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Car, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import {
  BODY_TYPE_OPTIONS,
  ENGINE_OPTIONS,
  TRANSMISSION_TYPE_OPTIONS,
  TRANSMISSION_SPEED_OPTIONS,
  YEAR_OPTIONS,
  CYLINDER_OPTIONS,
  VEHICLE_FEATURE_CATEGORIES,
  EXTERIOR_IMAGE_TYPE_OPTIONS,
  INTERIOR_IMAGE_TYPE_OPTIONS,
} from "../../../api/dealership/dealershipModel";
import {
  getVehicleMakes,
  getVehicleModels,
  getVehicleDetails,
  postVehicle,
  putVehicle,
} from "../../../api/dealership/dealershpService";
import { postAIVehicleImage } from "../../../api/agent/agentService";
import { compressImageForAI } from "../../../api/agent/aiImageHelper";

import { ViewBox } from "./components/ViewBox";
import { SelectButton } from "./components/SelectButton";
import { AutInput } from "./components/AutInput";
import { MultiLineInput } from "./components/MultiLineInput";
import { OptionPickerModal } from "./components/OptionPickerModal";
import { VehicleFeatureGroup } from "./components/VehicleFeatureGroup";
import { VehicleImages } from "./components/VehicleImages";
import { FileButton } from "./components/FileButton";
import { VehicleSavingLoader } from "./components/VehicleSavingLoader";
import { CustomAlertModal } from "./components/CustomAlertModal";
import { PrimaryButton } from "../../../components/buttons/primaryButton/PrimaryButton";

const TOUR_DURATION_OPTIONS = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "45 minutes", label: "45 minutes" },
  { value: "1 hour", label: "1 hour" },
  { value: "1.5 hours", label: "1.5 hours" },
  { value: "2 hours", label: "2 hours" },
];

const BUFFER_TIME_OPTIONS = [
  { value: "0 minutes", label: "None (0 min)" },
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "45 minutes", label: "45 minutes" },
  { value: "1 hour", label: "1 hour" },
];

export function VehicleModal({ vehicle, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEditMode = !!vehicle?.id;
  const modalScrollRef = useRef(null);

  // Loading and Alert States
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [alertText, setAlertText] = useState("");
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  // Field Error States (matching mobile VehicleDetails.tsx)
  const [makeError, setMakeError] = useState(false);
  const [modelError, setModelError] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [priceError, setPriceError] = useState(false);
  const [exteriorColorError, setExteriorColorError] = useState(false);
  const [interiorColorError, setInteriorColorError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [warrantyError, setWarrantyError] = useState(false);
  const [exteriorImagesError, setExteriorImagesError] = useState(false);
  const [interiorImagesError, setInteriorImagesError] = useState(false);

  // Form State
  const [vehicleType, setVehicleType] = useState("New");
  const [numberOfOwners, setNumberOfOwners] = useState("");
  const [numberOfAccidents, setNumberOfAccidents] = useState("");

  const [makes, setMakes] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);

  const [year, setYear] = useState("");
  const [mileage, setMileage] = useState("");
  const [bodyType, setBodyType] = useState("sedan");
  const [articleCode, setArticleCode] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const [price, setPrice] = useState("");
  const [leasingPrice, setLeasingPrice] = useState("");
  const [isDemo, setIsDemo] = useState(false);

  const [engine, setEngine] = useState("gasoline");
  const [engineOrigin, setEngineOrigin] = useState("");
  const [motorVolume, setMotorVolume] = useState("");
  const [motorCylinders, setMotorCylinders] = useState("");
  const [horsePower, setHorsePower] = useState("");
  const [torque, setTorque] = useState("");
  const [transmissionType, setTransmissionType] = useState("automatic");
  const [transmissionSpeed, setTransmissionSpeed] = useState("6");
  const [warranty, setWarranty] = useState("");

  const [exteriorColor, setExteriorColor] = useState("");
  const [interiorColor, setInteriorColor] = useState("");
  const [interiorType, setInteriorType] = useState("");

  const [viewingDuration, setViewingDuration] = useState("30 minutes");
  const [bufferTime, setBufferTime] = useState("15 minutes");

  const [exteriorImages, setExteriorImages] = useState([]);
  const [exteriorImagesRemove, setExteriorImagesRemove] = useState([]);
  const [exteriorImagesChangedLabel, setExteriorImagesChangedLabel] = useState([]);

  const [interiorImages, setInteriorImages] = useState([]);
  const [interiorImagesRemove, setInteriorImagesRemove] = useState([]);
  const [interiorImagesChangedLabel, setInteriorImagesChangedLabel] = useState([]);

  const [technicalDocument, setTechnicalDocument] = useState(null);
  const [technicalDocumentRemove, setTechnicalDocumentRemove] = useState(false);

  const [features, setFeatures] = useState({});

  // Active Picker Modal State
  const [activePicker, setActivePicker] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  // Localized option maps mirroring mobile hooks
  const localizedExteriorOptions = useMemo(() => {
    return EXTERIOR_IMAGE_TYPE_OPTIONS.map((item) => {
      const locKey = `listingLabels.${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedInteriorOptions = useMemo(() => {
    return INTERIOR_IMAGE_TYPE_OPTIONS.map((item) => {
      const locKey = `listingLabels.${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedBodyTypeOptions = useMemo(() => {
    return BODY_TYPE_OPTIONS.map((item) => {
      const locKey = `dealershipOptions.${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedEngineOptions = useMemo(() => {
    return ENGINE_OPTIONS.map((item) => {
      const locKey = `dealershipOptions.${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedTransmissionTypeOptions = useMemo(() => {
    return TRANSMISSION_TYPE_OPTIONS.map((item) => {
      const locKey = `dealershipOptions.${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedTransmissionSpeedOptions = useMemo(() => {
    return TRANSMISSION_SPEED_OPTIONS.map((item) => {
      const locKey = `dealershipOptions.speed${item.value.replace("+", "Plus")}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  const localizedCylinderOptions = useMemo(() => {
    return CYLINDER_OPTIONS.map((item) => {
      const locKey = `dealershipOptions.cylinders${item.value}`;
      const loc = t(locKey);
      return {
        ...item,
        label: loc && loc !== locKey ? loc : item.label,
      };
    });
  }, [t]);

  // Scroll to invalid field with smooth animation
  const scrollToField = (fieldId) => {
    setTimeout(() => {
      const el = document.getElementById(fieldId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80);
  };

  // Formatters matching mobile VehicleDetails.tsx
  const handleMileageChange = (e) => {
    const text = e.target.value;
    const prevDigits = mileage.replace(/\D/g, "");
    const rawDigits = text.replace(/\D/g, "");

    if (text.length < mileage.length && rawDigits === prevDigits) {
      const newDigits = prevDigits.slice(0, -1);
      setMileage(newDigits ? `${newDigits} KM` : "");
      return;
    }

    if (!rawDigits) {
      setMileage("");
      return;
    }

    setMileage(`${rawDigits} KM`);
  };

  const handleMotorVolumeChange = (e) => {
    const text = e.target.value;
    let cleaned = text.replace(/,/g, ".");
    const prevRaw = motorVolume.replace(/[^0-9.]/g, "");

    if (text.length < motorVolume.length) {
      let rawFromText = cleaned.replace(/[^0-9.]/g, "");
      if (rawFromText === prevRaw) {
        rawFromText = prevRaw.slice(0, -1);
      }
      if (!rawFromText || rawFromText === "0") {
        setMotorVolume("");
        return;
      }
      const parts = rawFromText.split(".");
      const normalized =
        parts[0] + (parts.length > 1 ? "." + parts.slice(1).join("") : "");
      setMotorVolume(`${normalized}L`);
      return;
    }

    cleaned = cleaned.replace(/[^0-9.]/g, "");

    if (cleaned.startsWith("0") || cleaned.startsWith(".")) {
      cleaned = cleaned.replace(/^[0.]+/g, "");
    }

    if (!cleaned) {
      setMotorVolume("");
      return;
    }

    const parts = cleaned.split(".");
    const integerPart = parts[0];
    const decimalPart =
      parts.length > 1 ? parts.slice(1).join("").slice(0, 1) : null;

    let result = integerPart;
    if (decimalPart !== null) {
      result += `.${decimalPart}`;
    }

    setMotorVolume(`${result}L`);
  };

  const handleHorsePowerChange = (e) => {
    const text = e.target.value;
    const prevDigits = horsePower.replace(/\D/g, "");
    const rawDigits = text.replace(/\D/g, "");

    if (text.length < horsePower.length && rawDigits === prevDigits) {
      const newDigits = prevDigits.slice(0, -1);
      setHorsePower(newDigits ? `${newDigits} HP` : "");
      return;
    }

    if (!rawDigits) {
      setHorsePower("");
      return;
    }

    setHorsePower(`${rawDigits} HP`);
  };

  const handleTorqueChange = (e) => {
    const text = e.target.value;
    const prevDigits = torque.replace(/\D/g, "");
    const rawDigits = text.replace(/\D/g, "");

    if (text.length < torque.length && rawDigits === prevDigits) {
      const newDigits = prevDigits.slice(0, -1);
      setTorque(newDigits ? `${newDigits} lb-pie` : "");
      return;
    }

    if (!rawDigits) {
      setTorque("");
      return;
    }

    setTorque(`${rawDigits} lb-pie`);
  };

  // Error message extractor matching mobile extractErrorMessage
  const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) {
      return (
        err?.message ||
        t("somethingWentWrong") ||
        "An error occurred while saving."
      );
    }
    if (typeof data === "string") {
      return data;
    }
    if (Array.isArray(data)) {
      return data
        .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
        .join("\n");
    }
    if (typeof data === "object") {
      if (typeof data.detail === "string") return data.detail;
      if (typeof data.message === "string") return data.message;
      if (typeof data.error === "string") return data.error;

      const lines = [];
      Object.entries(data).forEach(([key, val]) => {
        let fieldMessage = "";
        if (Array.isArray(val)) {
          fieldMessage = val
            .map((v) => (typeof v === "string" ? v : JSON.stringify(v)))
            .join(", ");
        } else if (typeof val === "string") {
          fieldMessage = val;
        } else if (typeof val === "object" && val !== null) {
          fieldMessage = JSON.stringify(val);
        }

        if (fieldMessage) {
          if (
            key === "non_field_errors" ||
            key === "detail" ||
            key === "error"
          ) {
            lines.push(fieldMessage);
          } else {
            lines.push(`${key}: ${fieldMessage}`);
          }
        }
      });

      if (lines.length > 0) {
        return lines.join("\n");
      }
    }
    return (
      err?.message ||
      t("somethingWentWrong") ||
      "An error occurred while saving."
    );
  };

  // Image handling
  const onPickExteriorImages = (files) => {
    setExteriorImagesError(false);
    const maxSize = 5 * 1024 * 1024;
    const valid = [];
    let oversized = false;

    files.forEach((file) => {
      if (file.size > maxSize) {
        oversized = true;
        return;
      }
      valid.push(file);
    });

    if (oversized) {
      setAlertText(t("profile.imageSizeError") || "Some images exceeded the 5MB size limit.");
      setIsAlertVisible(true);
    }

    if (valid.length > 0) {
      const initialItems = valid.map((file, idx) => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        tempId: `${Date.now()}_ext_${idx}_${Math.random()}`,
        label: "unknown",
        isLoading: true,
      }));

      setExteriorImages((prev) => [...prev, ...initialItems]);

      initialItems.forEach(async (item) => {
        let label = "unknown";
        try {
          const { base64, mimeType } = await compressImageForAI(item.file);
          const res = await postAIVehicleImage({
            category: "exterior",
            image_base64: base64,
            mime_type: mimeType || "image/jpeg",
          });
          if (res?.data?.label) {
            label = res.data.label;
          }
        } catch (e) {
          console.error("AI exterior image error:", e);
        } finally {
          setExteriorImages((prev) =>
            prev.map((img) =>
              img.tempId === item.tempId
                ? {
                    ...img,
                    label,
                    isLoading: false,
                  }
                : img
            )
          );
        }
      });
    }
  };

  const onRemoveExteriorImage = (index) => {
    const item = exteriorImages[index];
    if (item && typeof item.id === "number") {
      setExteriorImagesRemove((prev) => [...prev, item.id]);
      setExteriorImagesChangedLabel((prev) =>
        prev.filter((i) => i.id !== item.id)
      );
    }
    setExteriorImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onPickInteriorImages = (files) => {
    setInteriorImagesError(false);
    const maxSize = 5 * 1024 * 1024;
    const valid = [];
    let oversized = false;

    files.forEach((file) => {
      if (file.size > maxSize) {
        oversized = true;
        return;
      }
      valid.push(file);
    });

    if (oversized) {
      setAlertText(t("profile.imageSizeError") || "Some images exceeded the 5MB size limit.");
      setIsAlertVisible(true);
    }

    if (valid.length > 0) {
      const initialItems = valid.map((file, idx) => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        tempId: `${Date.now()}_int_${idx}_${Math.random()}`,
        label: "unknown",
        isLoading: true,
      }));

      setInteriorImages((prev) => [...prev, ...initialItems]);

      initialItems.forEach(async (item) => {
        let label = "unknown";
        try {
          const { base64, mimeType } = await compressImageForAI(item.file);
          const res = await postAIVehicleImage({
            category: "interior",
            image_base64: base64,
            mime_type: mimeType || "image/jpeg",
          });
          if (res?.data?.label) {
            label = res.data.label;
          }
        } catch (e) {
          console.error("AI interior image error:", e);
        } finally {
          setInteriorImages((prev) =>
            prev.map((img) =>
              img.tempId === item.tempId
                ? {
                    ...img,
                    label,
                    isLoading: false,
                  }
                : img
            )
          );
        }
      });
    }
  };

  const onRemoveInteriorImage = (index) => {
    const item = interiorImages[index];
    if (item && typeof item.id === "number") {
      setInteriorImagesRemove((prev) => [...prev, item.id]);
      setInteriorImagesChangedLabel((prev) =>
        prev.filter((i) => i.id !== item.id)
      );
    }
    setInteriorImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Technical Document
  const onPickDocument = (file) => {
    setTechnicalDocument({
      file,
      name: file.name,
    });
    setTechnicalDocumentRemove(false);
  };

  const onRemoveDocument = () => {
    if (isEditMode) {
      setTechnicalDocumentRemove(true);
    }
    setTechnicalDocument(null);
  };

  // Feature Toggle
  const onToggleFeature = (key) => {
    setFeatures((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Fetch Makes & Edit Details on Mount
  useEffect(() => {
    getVehicleMakes()
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || [];
        setMakes(data);
      })
      .catch((err) => {
        console.error("Error fetching vehicle makes:", err);
      });

    if (isEditMode && vehicle?.id) {
      setInitialLoading(true);
      getVehicleDetails(vehicle.id)
        .then((res) => {
          const data = res.data;
          if (!data) return;

          if (data.type) setVehicleType(data.type);
          if (data.number_of_owners !== undefined && data.number_of_owners !== null) {
            setNumberOfOwners(String(data.number_of_owners));
          }
          if (data.number_of_accidents !== undefined && data.number_of_accidents !== null) {
            setNumberOfAccidents(String(data.number_of_accidents));
          }

          if (data.make) {
            setSelectedMake({
              id: data.make,
              name: data.make_name || "",
            });
          }
          if (data.model) {
            setSelectedModel({
              id: data.model,
              name: data.model_name || "",
            });
          }

          if (data.year) setYear(String(data.year));
          if (data.mileage !== undefined && data.mileage !== null) {
            const raw = String(data.mileage).replace(/\D/g, "");
            setMileage(raw ? `${raw} KM` : "");
          }
          if (data.body_type) setBodyType(data.body_type);
          if (data.article_code) setArticleCode(data.article_code);
          if (data.serial_number) setSerialNumber(data.serial_number);
          if (data.description) setDescription(data.description);
          if (data.notes) setNotes(data.notes);

          if (data.price !== undefined && data.price !== null) setPrice(String(data.price));
          if (data.leasing_price !== undefined && data.leasing_price !== null) {
            setLeasingPrice(String(data.leasing_price));
          }
          if (data.is_demo !== undefined) setIsDemo(Boolean(data.is_demo));

          if (data.engine) setEngine(data.engine);
          if (data.engine_origin) setEngineOrigin(data.engine_origin);
          if (data.motor_volume) {
            const vol = data.motor_volume.endsWith("L")
              ? data.motor_volume
              : `${data.motor_volume}L`;
            setMotorVolume(vol);
          }
          if (data.motor_cylinders !== undefined && data.motor_cylinders !== null) {
            setMotorCylinders(String(data.motor_cylinders));
          }
          if (data.horse_power) {
            const hpDigits = String(data.horse_power).replace(/\D/g, "");
            setHorsePower(hpDigits ? `${hpDigits} HP` : "");
          }
          if (data.torque) {
            const torqueDigits = String(data.torque).replace(/\D/g, "");
            setTorque(torqueDigits ? `${torqueDigits} lb-pie` : data.torque);
          }
          if (data.transmission_type) setTransmissionType(data.transmission_type);
          if (data.transmission_speed) setTransmissionSpeed(String(data.transmission_speed));
          if (data.warranty) setWarranty(data.warranty);

          if (data.exterior_color) setExteriorColor(data.exterior_color);
          if (data.interior_color) setInteriorColor(data.interior_color);
          if (data.interior_type) setInteriorType(data.interior_type);

          if (data.viewing_duration) {
            const unit = data.viewing_duration_time_unit || "minutes";
            setViewingDuration(`${data.viewing_duration} ${unit}`);
          }
          if (data.buffer_time !== undefined && data.buffer_time !== null) {
            const unit = data.buffer_time_time_unit || "minutes";
            setBufferTime(`${data.buffer_time} ${unit}`);
          }

          if (data.exterior_images && Array.isArray(data.exterior_images)) {
            setExteriorImages(
              data.exterior_images.map((img) => ({
                ...img,
                label: img.label || "unknown",
              }))
            );
          } else if (data.images && Array.isArray(data.images)) {
            setExteriorImages(
              data.images.map((img) => ({
                ...img,
                label: img.label || "unknown",
              }))
            );
          }

          if (data.interior_images && Array.isArray(data.interior_images)) {
            setInteriorImages(
              data.interior_images.map((img) => ({
                ...img,
                label: img.label || "unknown",
              }))
            );
          }

          if (data.technical_document) {
            const fileName =
              typeof data.technical_document === "string"
                ? data.technical_document.split("/").pop() || "Technical Document"
                : "Technical Document";
            setTechnicalDocument({
              name: fileName,
              uri: data.technical_document,
            });
          }
          setTechnicalDocumentRemove(false);

          const loadedFeatures = {};
          VEHICLE_FEATURE_CATEGORIES.forEach((cat) => {
            cat.items.forEach((f) => {
              if (data[f.key] !== undefined) {
                loadedFeatures[f.key] = Boolean(data[f.key]);
              }
            });
          });
          setFeatures(loadedFeatures);
        })
        .catch((err) => {
          console.error("Error loading vehicle details:", err);
          toast.error(t("home.failedLoadDetails") || "Failed to load vehicle details");
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [isEditMode, vehicle?.id]);

  // Sync selectedMake icon / name when makes list arrives
  useEffect(() => {
    if (makes.length > 0 && selectedMake?.id && !selectedMake.name) {
      const found = makes.find((m) => m.id === selectedMake.id);
      if (found) setSelectedMake(found);
    }
  }, [makes, selectedMake]);

  // Fetch models whenever selectedMake changes
  useEffect(() => {
    setModels([]);
    if (selectedMake?.id) {
      setLoadingModels(true);
      getVehicleModels(selectedMake.id)
        .then((res) => {
          const data = Array.isArray(res.data)
            ? res.data
            : res.data?.results || [];
          setModels(data);
        })
        .catch((err) => {
          console.error("Error fetching vehicle models:", err);
        })
        .finally(() => {
          setLoadingModels(false);
        });
    } else {
      setLoadingModels(false);
    }
  }, [selectedMake]);

  // Label resolvers matching mobile getBodyTypeLabel, getEngineLabel, etc.
  const getBodyTypeLabel = () =>
    localizedBodyTypeOptions.find((o) => o.value === bodyType)?.label || bodyType;

  const getEngineLabel = () =>
    localizedEngineOptions.find((o) => o.value === engine)?.label || engine;

  const getCylindersLabel = () =>
    localizedCylinderOptions.find((o) => o.value === motorCylinders)?.label ||
    (motorCylinders
      ? `${motorCylinders} ${t("profile.cylinders") || "Cylinders"}`
      : t("profile.selectCylinders") || "Select Cylinders");

  const getTransmissionLabel = () =>
    localizedTransmissionTypeOptions.find((o) => o.value === transmissionType)
      ?.label || transmissionType;

  const getSpeedLabel = () =>
    localizedTransmissionSpeedOptions.find((o) => o.value === transmissionSpeed)
      ?.label || transmissionSpeed;

  // Validation & Save Handler (1-to-1 match with mobile onSave in VehicleDetails.tsx)
  const onSave = () => {
    if (!selectedMake) {
      setMakeError(true);
      scrollToField("field-make");
      setAlertText(t("profile.selectMakeFirst", "Please select a vehicle make first."));
      setIsAlertVisible(true);
      return;
    }

    if (!selectedModel) {
      setModelError(true);
      scrollToField("field-model");
      setAlertText(t("profile.selectModel", "Please select a model."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(year ?? "").trim()) {
      setYearError(true);
      scrollToField("field-year");
      setAlertText(t("profile.selectYear", "Please select a year."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(description ?? "").trim()) {
      setDescriptionError(true);
      scrollToField("field-description");
      setAlertText(t("home.descriptionRequired", "Description is required."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(price ?? "").trim()) {
      setPriceError(true);
      scrollToField("field-price");
      setAlertText(t("home.priceRequired", "Price is required."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(warranty ?? "").trim()) {
      setWarrantyError(true);
      scrollToField("field-warranty");
      setAlertText(t("profile.warrantyRequired", "Warranty information is required."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(exteriorColor ?? "").trim()) {
      setExteriorColorError(true);
      scrollToField("field-exteriorColor");
      setAlertText(t("profile.exteriorColorRequired", "Exterior color is required."));
      setIsAlertVisible(true);
      return;
    }

    if (!String(interiorColor ?? "").trim()) {
      setInteriorColorError(true);
      scrollToField("field-interiorColor");
      setAlertText(t("profile.interiorColorRequired", "Interior color is required."));
      setIsAlertVisible(true);
      return;
    }

    if (exteriorImages?.find((img) => !img.label || img.label === "unknown")) {
      setExteriorImagesError(true);
      scrollToField("field-exteriorImages");
      setAlertText(t("profile.selectExteriorImageTypes", "Please select labels for all exterior photos."));
      setIsAlertVisible(true);
      return;
    }

    if (interiorImages?.find((img) => !img.label || img.label === "unknown")) {
      setInteriorImagesError(true);
      scrollToField("field-interiorImages");
      setAlertText(t("profile.selectInteriorImageTypes", "Please select labels for all interior photos."));
      setIsAlertVisible(true);
      return;
    }

    const payload = {
      type: vehicleType,
      ...(vehicleType === "Pre-owned"
        ? {
            number_of_owners: numberOfOwners ? +numberOfOwners : null,
            number_of_accidents: numberOfAccidents ? +numberOfAccidents : null,
          }
        : {}),
      make: selectedMake.id,
      model: selectedModel.id,
      year: +String(year).replace(/\D/g, ""),
      mileage: mileage ? +String(mileage).replace(/\D/g, "") : null,
      body_type: bodyType,
      article_code: articleCode || null,
      serial_number: serialNumber || null,
      description,
      notes: notes || " ",
      price: +String(price).replace(/[^0-9.]/g, ""),
      leasing_price: leasingPrice ? +String(leasingPrice).replace(/[^0-9.]/g, "") : null,
      is_demo: isDemo,
      engine,
      engine_origin: engineOrigin || null,
      motor_volume: motorVolume ? motorVolume.replace(/\.L$/, "L") : null,
      motor_cylinders: motorCylinders ? +String(motorCylinders).replace(/\D/g, "") : null,
      horse_power: horsePower ? +String(horsePower).replace(/\D/g, "") : null,
      torque: torque || null,
      transmission_type: transmissionType,
      transmission_speed: transmissionSpeed,
      warranty,
      exterior_color: exteriorColor,
      interior_color: interiorColor,
      interior_type: interiorType || " ",
      viewing_duration: +viewingDuration.split(" ")[0] || 30,
      viewing_duration_time_unit: viewingDuration.includes("hour")
        ? "hours"
        : "minutes",
      buffer_time: +bufferTime.split(" ")[0] || 15,
      buffer_time_time_unit: bufferTime.includes("hour") ? "hours" : "minutes",
      exterior_images: exteriorImages,
      interior_images: interiorImages,
      technical_document: technicalDocument,
      ...(exteriorImagesRemove.length > 0
        ? { exterior_images_remove: exteriorImagesRemove }
        : {}),
      ...(exteriorImagesChangedLabel.length > 0
        ? { exterior_images_update_labels: exteriorImagesChangedLabel }
        : {}),
      ...(interiorImagesRemove.length > 0
        ? { interior_images_remove: interiorImagesRemove }
        : {}),
      ...(interiorImagesChangedLabel.length > 0
        ? { interior_images_update_labels: interiorImagesChangedLabel }
        : {}),
      ...(technicalDocumentRemove ? { technical_document_remove: true } : {}),
      ...features,
    };

    setLoading(true);
    const savePromise =
      isEditMode && vehicle?.id
        ? putVehicle(vehicle.id, payload)
        : postVehicle(payload);

    savePromise
      .then(() => {
        setLoading(false);
        toast.success(
          isEditMode
            ? t("profile.vehicleUpdated") || "Vehicle updated successfully!"
            : t("profile.vehicleCreated") || "Vehicle created successfully!"
        );
        onSaved && onSaved();
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        console.error("Save vehicle error:", err?.response?.data || err);
        const errorMsg = extractErrorMessage(err);
        setAlertText(errorMsg);
        setIsAlertVisible(true);
      });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 15 }}
        transition={{ type: "spring", damping: 25, stiffness: 320 }}
        className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden relative"
      >
        {/* Modal Top Header */}
        <div className="flex items-center justify-between px-6 py-4.5 border-b border-gray-100 bg-white sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#eef0f8] text-[#0B0E39] flex items-center justify-center font-bold">
              <Car className="w-5 h-5 text-[#020637]" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {isEditMode
                  ? t("profile.editVehicle", "Edit Vehicle")
                  : t("profile.addVehicle", "Add Vehicle")}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body: Single continuous scroll matching mobile VehicleDetails layout */}
        <div
          ref={modalScrollRef}
          className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 space-y-7"
        >
          {/* 1. Top Segmented Switch: New vs Pre-owned */}
          <div className="w-full flex justify-center">
            <div className="w-full max-w-md bg-[#F0F1F5] rounded-2xl p-1.5 flex shadow-inner">
              <button
                type="button"
                onClick={() => setVehicleType("New")}
                className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                  vehicleType === "New"
                    ? "bg-[#0B0E39] text-white shadow-sm"
                    : "text-[#767676] hover:text-gray-900 font-semibold"
                }`}
              >
                {t("profile.newVehicle") || "New Vehicle"}
              </button>
              <button
                type="button"
                onClick={() => setVehicleType("Pre-owned")}
                className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                  vehicleType === "Pre-owned"
                    ? "bg-[#0B0E39] text-white shadow-sm"
                    : "text-[#767676] hover:text-gray-900 font-semibold"
                }`}
              >
                {t("profile.preOwned") || "Pre-owned"}
              </button>
            </div>
          </div>

          {/* 2. Vehicle History (Pre-owned only) */}
          {vehicleType === "Pre-owned" && (
            <ViewBox label={t("profile.vehicleHistory") || "Vehicle History"}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <AutInput
                  label={t("profile.owners") || "Number of Owners"}
                  placeholder="e.g. 1"
                  value={numberOfOwners}
                  onChange={(e) => setNumberOfOwners(e.target.value.replace(/\D/g, ""))}
                  type="text"
                />
                <AutInput
                  label={t("profile.accidents") || "Number of Accidents"}
                  placeholder="e.g. 0"
                  value={numberOfAccidents}
                  onChange={(e) => setNumberOfAccidents(e.target.value.replace(/\D/g, ""))}
                  type="text"
                />
              </div>
            </ViewBox>
          )}

          {/* 3. Basic Information */}
          <ViewBox label={t("profile.basicInformation") || "Basic Information"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div id="field-make">
                <SelectButton
                  label={t("profile.make") || "Make"}
                  obligatorily
                  error={makeError}
                  text={selectedMake?.name || t("profile.selectMake") || "Select Make"}
                  icon={selectedMake?.icon}
                  placeholder={!selectedMake}
                  onClick={() => {
                    setMakeError(false);
                    setActivePicker("make");
                  }}
                />
              </div>

              <div id="field-model">
                <SelectButton
                  label={t("profile.model") || "Model"}
                  obligatorily
                  error={modelError}
                  disabled={!selectedMake || models.length === 0 || loadingModels}
                  text={
                    loadingModels
                      ? t("profile.loadingModels") || "Loading models..."
                      : selectedModel?.name ||
                        (selectedMake && models.length === 0
                          ? t("profile.noModels") || "No models available"
                          : t("profile.selectModel") || "Select Model")
                  }
                  placeholder={!selectedModel}
                  onClick={() => {
                    if (!selectedMake) {
                      setMakeError(true);
                      scrollToField("field-make");
                      setAlertText(t("profile.selectMakeFirst") || "Please select a vehicle make first.");
                      setIsAlertVisible(true);
                      return;
                    }
                    if (models.length === 0) return;
                    setModelError(false);
                    setActivePicker("model");
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div id="field-year">
                <SelectButton
                  label={t("profile.year") || "Year"}
                  obligatorily
                  error={yearError}
                  text={year || t("profile.selectYear") || "Select Year"}
                  placeholder={!year}
                  onClick={() => {
                    setYearError(false);
                    setActivePicker("year");
                  }}
                />
              </div>

              <AutInput
                label={t("profile.mileage") || "Mileage"}
                placeholder="e.g. 15000 KM"
                value={mileage}
                onChange={handleMileageChange}
              />
            </div>

            <SelectButton
              label={t("profile.bodyType") || "Body Type"}
              text={getBodyTypeLabel()}
              onClick={() => setActivePicker("body_type")}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <AutInput
                label={t("profile.articleCode") || "Article Code"}
                placeholder="e.g. ART-9021"
                value={articleCode}
                onChange={(e) => setArticleCode(e.target.value)}
              />
              <AutInput
                label={t("profile.serialNumber") || "Serial Number (VIN)"}
                placeholder="17-digit VIN"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
              />
            </div>

            <div id="field-description">
              <MultiLineInput
                label={t("profile.description") || "Description"}
                obligatorily
                description={description}
                serviceDescriptionError={descriptionError}
                placeHolder={t("profile.vehicleDescriptionPlaceholder") || "Describe the condition, key specs, history, and highlights..."}
                onServiceDescriptionChange={(val) => {
                  setDescription(val);
                  setDescriptionError(false);
                }}
                onFocus={() => setDescriptionError(false)}
              />
            </div>
          </ViewBox>

          {/* 4. Pricing & Status */}
          <ViewBox label={t("profile.pricingStatus") || "Pricing & Status"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div id="field-price">
                <AutInput
                  label={t("profile.price") || "Price"}
                  obligatorily
                  error={priceError}
                  placeholder="e.g. 54000"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value.replace(/\D/g, ""));
                    setPriceError(false);
                  }}
                  onFocus={() => setPriceError(false)}
                />
              </div>

              <AutInput
                label={t("profile.leasingPrice") || "Leasing Price (monthly)"}
                placeholder="e.g. 650"
                value={leasingPrice}
                onChange={(e) => setLeasingPrice(e.target.value.replace(/\D/g, ""))}
              />
            </div>

            {/* Demo Vehicle Toggle Switch */}
            <div className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-gray-200">
              <div className="flex-1 pr-3">
                <p className="text-sm font-semibold text-gray-900">
                  {t("profile.demoVehicle") || "Demo Vehicle"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("profile.demoVehicleDescription") || "Display this vehicle as a dealership test-drive unit"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDemo(!isDemo)}
                className={`w-12 h-6 rounded-full transition-colors relative p-0.5 outline-none flex items-center ${
                  isDemo ? "bg-[#3BD97A]" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    isDemo ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </ViewBox>

          {/* 5. Engine & Transmission */}
          <ViewBox label={t("profile.engineTransmission") || "Engine & Transmission"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <SelectButton
                label={t("profile.engine") || "Engine"}
                text={getEngineLabel()}
                onClick={() => setActivePicker("engine")}
              />
              <AutInput
                label={t("profile.engineOrigin") || "Engine Origin"}
                placeholder="e.g. USA / Japan"
                value={engineOrigin}
                onChange={(e) => setEngineOrigin(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <AutInput
                label={t("profile.motorVolume") || "Motor Volume"}
                placeholder="e.g. 6.2L"
                value={motorVolume}
                onChange={handleMotorVolumeChange}
              />
              <SelectButton
                label={t("profile.cylinders") || "Cylinders"}
                text={getCylindersLabel()}
                onClick={() => setActivePicker("cylinders")}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <AutInput
                label={t("profile.horsepower") || "Horsepower"}
                placeholder="e.g. 425 HP"
                value={horsePower}
                onChange={handleHorsePowerChange}
              />
              <AutInput
                label={t("profile.torque") || "Torque"}
                placeholder="e.g. 460 lb-pie"
                value={torque}
                onChange={handleTorqueChange}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <SelectButton
                label={t("profile.transmission") || "Transmission"}
                text={getTransmissionLabel()}
                onClick={() => setActivePicker("transmission_type")}
              />
              <SelectButton
                label={t("profile.speeds") || "Speeds"}
                text={getSpeedLabel()}
                onClick={() => setActivePicker("transmission_speed")}
              />
            </div>

            <div id="field-warranty">
              <AutInput
                label={t("profile.warranty") || "Warranty"}
                obligatorily
                error={warrantyError}
                placeholder={t("profile.warrantyPlaceholder") || "e.g. 3 years or 100,000 km"}
                value={warranty}
                onChange={(e) => {
                  setWarranty(e.target.value);
                  setWarrantyError(false);
                }}
                onFocus={() => setWarrantyError(false)}
              />
            </div>
          </ViewBox>

          {/* 6. Colors & Interior */}
          <ViewBox label={t("profile.colorsInterior") || "Colors & Interior"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div id="field-exteriorColor">
                <AutInput
                  label={t("profile.exteriorColor") || "Exterior Color"}
                  obligatorily
                  error={exteriorColorError}
                  placeholder="e.g. Black Gloss"
                  value={exteriorColor}
                  onChange={(e) => {
                    setExteriorColor(e.target.value);
                    setExteriorColorError(false);
                  }}
                  onFocus={() => setExteriorColorError(false)}
                />
              </div>

              <div id="field-interiorColor">
                <AutInput
                  label={t("profile.interiorColor") || "Interior Color"}
                  obligatorily
                  error={interiorColorError}
                  placeholder="e.g. Jet Black"
                  value={interiorColor}
                  onChange={(e) => {
                    setInteriorColor(e.target.value);
                    setInteriorColorError(false);
                  }}
                  onFocus={() => setInteriorColorError(false)}
                />
              </div>
            </div>

            <AutInput
              label={t("profile.interiorType") || "Interior Type"}
              placeholder="e.g. Perforated Leather / Alcantara"
              value={interiorType}
              onChange={(e) => setInteriorType(e.target.value)}
            />
          </ViewBox>

          {/* 7. Features & Equipment */}
          <ViewBox label={t("profile.featuresEquipment") || "Features & Equipment"}>
            {VEHICLE_FEATURE_CATEGORIES.map((category, idx) => (
              <VehicleFeatureGroup
                key={category.id}
                category={category}
                selectedFeatures={features}
                onToggleFeature={onToggleFeature}
                defaultExpanded={idx === 0}
              />
            ))}
          </ViewBox>

          {/* 8. Viewing & Schedule Settings */}
          <ViewBox label={t("profile.viewingSchedule") || "Viewing & Schedule Settings"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <SelectButton
                label={t("profile.viewingDuration") || "Viewing Duration"}
                text={viewingDuration}
                onClick={() => setActivePicker("viewing_duration")}
              />
              <SelectButton
                label={t("profile.bufferTime") || "Buffer Time"}
                text={bufferTime}
                onClick={() => setActivePicker("buffer_time")}
              />
            </div>

            <MultiLineInput
              placeHolder={t("profile.notesPlaceholder") || "Special viewing instructions or parking notes for customers..."}
              description={notes}
              onServiceDescriptionChange={setNotes}
              rows={3}
            />
          </ViewBox>

          {/* 9. Exterior Photos */}
          <div id="field-exteriorImages">
            <ViewBox label={t("profile.exteriorPhotos") || "Exterior Photos"}>
              <VehicleImages
                images={exteriorImages}
                onAddFiles={onPickExteriorImages}
                onRemoveVehicleImage={onRemoveExteriorImage}
                onOpenLabelPicker={(index) => {
                  setActiveImageIndex(index);
                  setActivePicker("exterior_image_label");
                }}
                error={exteriorImagesError}
                buttonTitle={t("profile.uploadExteriorPhotos") || "Upload Exterior Photos"}
                errorMessage={t("profile.selectExteriorImageTypes") || "Please select labels for all exterior photos"}
                typeOptions={EXTERIOR_IMAGE_TYPE_OPTIONS}
              />
            </ViewBox>
          </div>

          {/* 10. Interior Photos */}
          <div id="field-interiorImages">
            <ViewBox label={t("profile.interiorPhotos") || "Interior Photos"}>
              <VehicleImages
                images={interiorImages}
                onAddFiles={onPickInteriorImages}
                onRemoveVehicleImage={onRemoveInteriorImage}
                onOpenLabelPicker={(index) => {
                  setActiveImageIndex(index);
                  setActivePicker("interior_image_label");
                }}
                error={interiorImagesError}
                buttonTitle={t("profile.uploadInteriorPhotos") || "Upload Interior Photos"}
                errorMessage={t("profile.selectInteriorImageTypes") || "Please select labels for all interior photos"}
                typeOptions={INTERIOR_IMAGE_TYPE_OPTIONS}
              />
            </ViewBox>
          </div>

          {/* 11. Documentation */}
          <ViewBox label={t("profile.documentation") || "Documentation"}>
            <FileButton
              title={t("profile.uploadTechnicalDocument") || "Upload Technical Document"}
              isFileSelected={technicalDocument !== null}
              url={technicalDocument?.uri}
              fileName={technicalDocument?.name || t("profile.document") || "Document"}
              onDelete={onRemoveDocument}
              onUploadFile={onPickDocument}
            />
          </ViewBox>

          {/* 12. Bottom Primary Submit Button */}
          <div className="pt-2 pb-6">
            <button
              type="button"
              disabled={loading}
              onClick={onSave}
              className="w-full py-4 px-6 text-base font-bold bg-[#0B0E39] hover:bg-[#1E2260] active:scale-[0.99] text-white rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin text-white" />}
              <span>
                {loading
                  ? t("loading") || "Saving..."
                  : isEditMode
                  ? t("profile.saveChanges") || "Save Changes"
                  : t("profile.createVehicleListing") || "Create Vehicle Listing"}
              </span>
            </button>
          </div>
        </div>

        {/* Option Picker Modals */}
        <OptionPickerModal
          isOpen={activePicker === "make"}
          title={t("profile.selectMake") || "Select Make"}
          options={makes.map((m) => ({
            value: String(m.id),
            label: m.name,
            icon: m.icon,
          }))}
          selectedValue={selectedMake ? String(selectedMake.id) : ""}
          onSelect={(val) => {
            const found = makes.find((m) => String(m.id) === String(val));
            if (found) {
              setSelectedMake(found);
              setSelectedModel(null);
              setMakeError(false);
            }
          }}
          onClose={() => setActivePicker(null)}
          showSearch={true}
        />

        <OptionPickerModal
          isOpen={activePicker === "model"}
          title={t("profile.selectModel") || "Select Model"}
          options={models.map((m) => ({
            value: String(m.id),
            label: m.name,
          }))}
          selectedValue={selectedModel ? String(selectedModel.id) : ""}
          onSelect={(val) => {
            const found = models.find((m) => String(m.id) === String(val));
            if (found) {
              setSelectedModel(found);
              setModelError(false);
            }
          }}
          onClose={() => setActivePicker(null)}
          showSearch={true}
        />

        <OptionPickerModal
          isOpen={activePicker === "year"}
          title={t("profile.selectYear") || "Select Year"}
          options={YEAR_OPTIONS}
          selectedValue={year}
          onSelect={(val) => {
            setYear(val);
            setYearError(false);
          }}
          onClose={() => setActivePicker(null)}
          showSearch={true}
        />

        <OptionPickerModal
          isOpen={activePicker === "body_type"}
          title={t("profile.selectBodyType") || "Select Body Type"}
          options={localizedBodyTypeOptions}
          selectedValue={bodyType}
          onSelect={(val) => setBodyType(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "engine"}
          title={t("profile.selectEngineType") || "Select Engine Type"}
          options={localizedEngineOptions}
          selectedValue={engine}
          onSelect={(val) => setEngine(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "cylinders"}
          title={t("profile.selectCylinders") || "Select Cylinders"}
          options={localizedCylinderOptions}
          selectedValue={motorCylinders}
          onSelect={(val) => setMotorCylinders(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "transmission_type"}
          title={t("profile.selectTransmissionType") || "Select Transmission Type"}
          options={localizedTransmissionTypeOptions}
          selectedValue={transmissionType}
          onSelect={(val) => setTransmissionType(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "transmission_speed"}
          title={t("profile.selectTransmissionSpeeds") || "Select Transmission Speeds"}
          options={localizedTransmissionSpeedOptions}
          selectedValue={transmissionSpeed}
          onSelect={(val) => setTransmissionSpeed(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "viewing_duration"}
          title={t("profile.viewingDuration") || "Viewing Duration"}
          options={TOUR_DURATION_OPTIONS}
          selectedValue={viewingDuration}
          onSelect={(val) => setViewingDuration(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "buffer_time"}
          title={t("profile.bufferTime") || "Buffer Time"}
          options={BUFFER_TIME_OPTIONS}
          selectedValue={bufferTime}
          onSelect={(val) => setBufferTime(val)}
          onClose={() => setActivePicker(null)}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "exterior_image_label"}
          title={t("bottomSheet.chooseType") || "Choose Photo Label"}
          options={localizedExteriorOptions}
          selectedValue={
            activeImageIndex !== null
              ? exteriorImages[activeImageIndex]?.label
              : undefined
          }
          onSelect={(val) => {
            if (activeImageIndex === null) return;
            const currentItem = exteriorImages[activeImageIndex];
            if (!currentItem) return;

            setExteriorImages((prev) =>
              prev.map((img, i) =>
                i === activeImageIndex ? { ...img, label: val } : img
              )
            );

            if (typeof currentItem.id === "number") {
              setExteriorImagesChangedLabel((prev) => {
                const exists = prev.find((i) => i.id === currentItem.id);
                if (exists) {
                  return prev.map((i) =>
                    i.id === currentItem.id ? { id: currentItem.id, label: val } : i
                  );
                }
                return [...prev, { id: currentItem.id, label: val }];
              });
            }

            setExteriorImagesError(false);
            setActivePicker(null);
            setActiveImageIndex(null);
          }}
          onClose={() => {
            setActivePicker(null);
            setActiveImageIndex(null);
          }}
          showSearch={false}
        />

        <OptionPickerModal
          isOpen={activePicker === "interior_image_label"}
          title={t("bottomSheet.chooseType") || "Choose Photo Label"}
          options={localizedInteriorOptions}
          selectedValue={
            activeImageIndex !== null
              ? interiorImages[activeImageIndex]?.label
              : undefined
          }
          onSelect={(val) => {
            if (activeImageIndex === null) return;
            const currentItem = interiorImages[activeImageIndex];
            if (!currentItem) return;

            setInteriorImages((prev) =>
              prev.map((img, i) =>
                i === activeImageIndex ? { ...img, label: val } : img
              )
            );

            if (typeof currentItem.id === "number") {
              setInteriorImagesChangedLabel((prev) => {
                const exists = prev.find((i) => i.id === currentItem.id);
                if (exists) {
                  return prev.map((i) =>
                    i.id === currentItem.id ? { id: currentItem.id, label: val } : i
                  );
                }
                return [...prev, { id: currentItem.id, label: val }];
              });
            }

            setInteriorImagesError(false);
            setActivePicker(null);
            setActiveImageIndex(null);
          }}
          onClose={() => {
            setActivePicker(null);
            setActiveImageIndex(null);
          }}
          showSearch={false}
        />

        {/* Custom Alert Modal for Errors and Validation Alerts */}
        <CustomAlertModal
          visible={isAlertVisible}
          text={alertText}
          onClose={() => setIsAlertVisible(false)}
          onDonePress={() => setIsAlertVisible(false)}
          onSingleButton={true}
          doneText={t("done") || "Done"}
        />

        {/* Full-screen frosted loader when saving */}
        <VehicleSavingLoader visible={loading} />
      </motion.div>
    </div>
  );
}

export default VehicleModal;
